import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import type { PageableContent, UUID } from '@cobliteam/common-types';

import AlertsRequester from '@cobliteam/api-lib/src/dashboard/alerts';
import type { TriggerEvent, TriggerEventNotification } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/events';
import type { Rule, RuleMode, TriggerRuleSummary } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { Trigger, TriggerRecord } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/triggers';
import type { TriggerUserPreferencesRecord } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/users';
import type { TriggerEventsFilters, TriggersFilters } from '@cobliteam/api-lib/src/dashboard/alerts/utils';

import { Environment } from '@cobliteam/react-environments';

import Alert from '@cobliteam/ui/components/Alert';

import useUser from '../useUser';

const env = Environment.getInstance().getEnvironment();
const alertsRequester = new AlertsRequester({ host: env.alertsApiHost });
const QUERY_KEY = 'ALERTS';

const useCreateTrigger = () =>
  useMutation({
    mutationFn: alertsRequester.createTriggger,
    meta: {
      successMessage: 'Regra criada com sucesso!',
      errorMessage: 'Erro ao criar regra!',
    },
  });

const useCreateObservationPeriodTrigger = () =>
  useMutation({
    mutationFn: alertsRequester.createObservationPeriodTrigger,
  });

const useEditTrigger = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      triggerId,
      triggerRecord,
    }: {
      triggerId: UUID;
      triggerRecord: TriggerRecord;
    }) => alertsRequester.editTrigger(triggerId, triggerRecord),
    onSuccess: (response) => {
      Alert.success('Regra editada com sucesso!');

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'TRIGGER', response.data.id],
      });
    },
    onError: () => Alert.error('Erro ao editar regra!'),
  });
};

const useGetRules = (mode?: RuleMode, options?: Partial<UseQueryOptions<Rule[]>>) =>
  useQuery({
    queryKey: [QUERY_KEY, 'RULES', mode],
    queryFn: () => alertsRequester.getRules(mode),
    ...options,
  });

const useGetRule = (ruleId: UUID, options?: Partial<UseQueryOptions<Rule>>) =>
  useQuery({
    queryKey: [QUERY_KEY, 'RULES', ruleId],
    queryFn: () => alertsRequester.getRule(ruleId),
    enabled: false,
    ...options,
  });

const useGetTriggers = (filters?: TriggersFilters, options?: Partial<UseQueryOptions<PageableContent<Trigger>>>) => {
  const session = useUser().getSession();

  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGERS', filters, session.fleet.id],
    queryFn: () => alertsRequester.getTriggers(filters),
    meta: {
      errorMessage: 'Erro ao buscar regra!',
    },
    ...options,
  });
};

const useGetTrigger = (triggerId: UUID, options?: Partial<UseQueryOptions<Trigger>>) => {
  const session = useUser().getSession();

  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGER', triggerId, session.fleet.id],
    queryFn: () => alertsRequester.getTrigger(triggerId),
    meta: {
      errorMessage: 'Erro ao buscar regra!',
    },
    ...options,
  });
};

const useActiveTrigger = (triggerId: UUID, active: boolean) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => alertsRequester.activeTrigger(triggerId, active),
    onSuccess: async () => {
      const label = active ? 'ativada' : 'desativada';
      Alert.success(`Regra ${label} com sucesso!`);

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'TRIGGERS'],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'TRIGGER', triggerId],
      });
    },
    onError: () => {
      const label = active ? 'ativar' : 'desativar';
      Alert.error(`Erro ao ${label} regra!`);
    },
  });
};

const useGetTriggerEvents = (filters: TriggerEventsFilters, options?: Partial<UseQueryOptions<PageableContent<TriggerEvent>>>) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGER_EVENTS', filters],
    queryFn: () => {
      try {
        return alertsRequester.getTriggerEvents(filters);
      } catch (error) {
        Alert.error('Erro ao buscar eventos!');

        throw error;
      }
    },
    enabled: !!filters.rule,
    ...options,
  });
};

const useGetTriggerUserPreferences = () => {
  const userId = useUser().getUser().id;

  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGER_USER', userId],
    queryFn: () => {
      try {
        return alertsRequester.getTriggerUserPreferences(userId);
      } catch (error) {
        Alert.error('Erro ao buscar usuário!');

        throw error;
      }
    },
  });
};

const useUpdateTriggerUserPreferences = () => {
  const queryClient = useQueryClient();
  const userId = useUser().getUser().id;

  return useMutation({
    mutationFn: (preferences: TriggerUserPreferencesRecord) => alertsRequester.updateTriggerUserPreferences(userId, preferences),
    onMutate: async (preferences: TriggerUserPreferencesRecord) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEY, 'TRIGGER_USER', userId],
      });

      const previousPreferences = queryClient.getQueryData([QUERY_KEY, 'TRIGGER_USER', userId]);

      queryClient.setQueryData([QUERY_KEY, 'TRIGGER_USER', userId], preferences);

      return { previousPreferences };
    },
    onSuccess: () => {
      Alert.success('Preferências atualizadas com sucesso!');
    },
    onError: (err, preferences, context) => {
      queryClient.setQueryData([QUERY_KEY, 'TRIGGER_USER', userId], context?.previousPreferences);
      Alert.error('Erro ao atualizar as preferências!');
    },
  });
};

const useGetTriggerEventsByRule = (startTime: string, endTime: string, mode?: RuleMode, options?: Partial<UseQueryOptions<TriggerRuleSummary[]>>) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'TRIGGER_EVENTS_BY_RULE', startTime, endTime, mode],
    queryFn: () => alertsRequester.getTriggerEventsByRule(startTime, endTime, mode),
    ...options,
  });
};

const useGetTriggersNotifications = (filters: TriggerEventsFilters) => {
  return useInfiniteQuery<PageableContent<TriggerEventNotification>>({
    queryKey: [QUERY_KEY, 'NOTIFICATIONS', filters],
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      alertsRequester.getTriggerNotifications({
        ...filters,
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.last === false) return pages.length;
      return undefined;
    },
  });
};

const useGetUnreadNotificationsAmount = (startTime: string, endTime: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'UNREAD_NOTIFICATIONS_AMOUNT'],
    queryFn: () => alertsRequester.getUnreadNotificationsAmount(startTime, endTime),
  });
};

const useMarkNotificationAsRead = (id: number, createdAt: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => alertsRequester.markNotificationAsRead(id, createdAt),
    onMutate: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MARK_NOTIFICATION_AS_READ', id],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'UNREAD_NOTIFICATIONS_AMOUNT'],
      });
    },
  });
};

const useMarkAllNotificationsAsRead = (startTime: string, endTime: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => alertsRequester.markAllNotificationsAsRead(startTime, endTime),
    onMutate: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MARK_ALL_NOTIFICATIONS_AS_READ'],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'UNREAD_NOTIFICATIONS_AMOUNT'],
      });
    },
  });
};

export {
  useCreateTrigger,
  useCreateObservationPeriodTrigger,
  useGetRules,
  useGetRule,
  useGetTrigger,
  useGetTriggers,
  useActiveTrigger,
  useGetTriggerEvents,
  useGetTriggerUserPreferences,
  useUpdateTriggerUserPreferences,
  useEditTrigger,
  useGetTriggerEventsByRule,
  useGetTriggersNotifications,
  useGetUnreadNotificationsAmount,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
};
